
import { motion } from "framer-motion";
import { Briefcase, Filter, CheckSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface StepProps {
  icon: React.ReactNode;
  number: number;
  title: string;
  text: string;
  delay?: number;
}

const Step = ({ icon, number, title, text, delay = 0 }: StepProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, delay }}
      viewport={{ once: true }}
      className="flex flex-col items-center text-center px-4 relative"
    >
      {/* Timeline Node */}
      <motion.div 
        className="relative z-10 mb-8 p-6 bg-white rounded-2xl shadow-lg border border-gray-100 group hover:shadow-xl transition-all duration-300"
        whileHover={{ scale: 1.05, y: -5 }}
      >
        <div className="mb-4 p-4 bg-primary/10 rounded-xl text-primary group-hover:bg-primary/20 transition-colors inline-block">
          {icon}
        </div>
        <motion.div 
          className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/80 text-white text-lg font-bold mb-4 mx-auto shadow-lg"
          animate={{ rotate: [0, 5, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {number}
        </motion.div>
        <h3 className="text-xl font-bold mb-3 text-gray-900">{title}</h3>
        <p className="text-gray-600 leading-relaxed">{text}</p>
      </motion.div>
    </motion.div>
  );
};

export const HowItWorksSection = () => {
  const navigate = useNavigate();
  
  return (
    <section className="py-20 bg-white border-t border-gray-100">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl font-bold mb-4">How It Works:</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Three simple steps to get matched with the right PEO.
          </p>
        </motion.div>

        {/* Horizontal Timeline Layout */}
        <div className="relative max-w-6xl mx-auto mb-16">
          {/* Timeline Line */}
          <div className="hidden md:block absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/20 via-primary/60 to-primary/20"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
              <Step
                icon={<Briefcase size={32} />}
                number={1}
                title="Tell Us About Your Organization"
                text="Just the basics—we'll handle the hard stuff."
                delay={0.1}
              />
              
              <Step
                icon={<Filter size={32} />}
                number={2}
                title="AI Finds Your Perfect Match"
                text="Our patent-pending AI analyzes 100+ data points to find PEOs perfectly suited to your organization."
                delay={0.2}
              />
              
              <Step
                icon={<CheckSquare size={32} />}
                number={3}
                title="Compare & Choose"
                text="Review your quotes with everything you need to make the right call."
                delay={0.3}
              />
            </div>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Button
            size="lg"
            onClick={() => navigate("/signup")}
            className="bg-primary hover:bg-primary/90 text-white text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Start Your Quote Search
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
